import type { CaptureResult } from 'posthog-js';

/**
 * Keeps a real Digest issue id out of analytics (D51f). `/dashboard/issues/116`
 * is a member's own record id, so wherever it would leave the app inside an
 * event, it is rewritten to the route template `/dashboard/issues/$id`.
 *
 * WHY a `before_send` scrub and not the router's PAGE_VIEW capture: posthog-js
 * adds the URL by itself, to events the router never sees — `$pathname`,
 * `$prev_pageview_pathname`, `$pageleave`'s URL and pathname,
 * `$session_entry_*`, `$referrer`, autocapture's `$current_url` and
 * `$elements[].attr__href`, the `$set`/`$set_once` person properties that
 * carry `$initial_*` copies of the same URLs, and the `$$heatmap` event, whose
 * `$heatmap_data` is `{ [location.href]: [...] }` (the URL is a KEY). Templating
 * one capture call cannot cover those; a single hook on the way out covers the
 * ones above.
 *
 * It walks every string VALUE and every object KEY rather than an enumerated
 * property list, so the covered set is "any id-bearing issue path that appears
 * as a string anywhere in the event's properties, `$set` or `$set_once`" (to
 * `MAX_DEPTH` levels), not a list of names. That is what it covers for the
 * posthog-js version in use (1.315.1) and for a later version that puts such a
 * path into a string value or a key; it is NOT a guarantee about payloads that
 * carry the path in some other form (encoded, split across strings, or inside a
 * skipped key). When a scrubbed key collides with another (two heatmap URLs that
 * differ only by id), array values are concatenated; for any other value type
 * the later one wins.
 *
 * Only the id digits change: query strings, hashes, other routes
 * (`/profile/123`, `/dashboard/issues` itself) and non-matching text pass
 * through untouched.
 *
 * NOT covered (out of this hook's reach): session-recording snapshots, whose
 * `$snapshot_data` is skipped here on purpose (large rrweb payloads, and they
 * are not property values) — recordings can show the URL/DOM — and the raw
 * transaction URL Sentry records. Both are platform-wide concerns.
 */

/** The route template an id-bearing path is rewritten to. */
export const ISSUE_ID_TEMPLATE = '$id';

/** `/dashboard/issues/` followed by a run of digits (an id). */
const ISSUE_PATH_WITH_ID = /(\/dashboard\/issues\/)\d+/g;

/** Keys never walked: rrweb snapshot payloads are huge and are not URL/path properties. */
const SKIPPED_KEYS: ReadonlySet<string> = new Set(['$snapshot_data']);

/** A guard against a pathological (cyclic or very deep) property bag; real events are 2-3 levels deep. */
const MAX_DEPTH = 6;

/** Rewrites `/dashboard/issues/<digits>` to `/dashboard/issues/$id` inside one string. */
export function scrubIssueIdsInString(value: string): string {
  return value.replace(ISSUE_PATH_WITH_ID, (_match, prefix: string) => `${prefix}${ISSUE_ID_TEMPLATE}`);
}

function scrubValue(value: unknown, depth: number): unknown {
  if (typeof value === 'string') return scrubIssueIdsInString(value);
  if (depth >= MAX_DEPTH || value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map((item) => scrubValue(item, depth + 1));
  // Only plain objects are copied; a Date, Map, DOM node etc. is left alone.
  const proto = Object.getPrototypeOf(value);
  if (proto !== Object.prototype && proto !== null) return value;
  // Keys are scrubbed as well as values: posthog-js's `$heatmap_data` is `{ [location.href]: [...] }`, so the URL
  // (and with it an issue id) is a KEY. Two keys can collapse into one (`.../116` and `.../117` both become
  // `.../$id`): array values are concatenated, in order, so no heatmap point is dropped; any other value type
  // cannot be merged meaningfully and the later one wins.
  // Collected in a Map, not a plain `{}`: an own `__proto__` key (JSON.parse makes one) assigned onto a plain object
  // sets its prototype instead and vanishes from the JSON; a Map keeps it, and `Object.fromEntries` defines it as
  // an own property. The Map also lets a collision `push` onto the existing array instead of re-spreading it.
  const out = new Map<string, unknown>();
  // Arrays this call built itself, the only ones a collision may `push` onto; a skipped key's value is the caller's
  // own array (kept by reference, never copied), so a collision onto it copies first rather than mutating the input.
  const owned = new Set<unknown[]>();
  for (const [key, inner] of Object.entries(value)) {
    const skipped = SKIPPED_KEYS.has(key);
    const scrubbedKey = skipped ? key : scrubIssueIdsInString(key);
    const scrubbedInner = skipped ? inner : scrubValue(inner, depth + 1);
    const existing = out.get(scrubbedKey);
    if (Array.isArray(existing) && Array.isArray(scrubbedInner)) {
      const target = owned.has(existing) ? existing : [...existing];
      owned.add(target);
      for (const item of scrubbedInner) target.push(item);
      out.set(scrubbedKey, target);
    } else {
      out.set(scrubbedKey, scrubbedInner);
      if (!skipped && Array.isArray(scrubbedInner)) owned.add(scrubbedInner);
    }
  }
  return Object.fromEntries(out);
}

/** Returns a copy of an event's property bag with every id-bearing issue path templated. */
export function scrubIssueIdProperties<T extends Record<string, unknown>>(properties: T): T {
  return scrubValue(properties, 0) as T;
}

/**
 * The single PostHog `before_send` hook (`posthog.init({ before_send })`).
 * Scrubs the three places an event carries data: its `properties` (which
 * includes any nested `$set`/`$set_once`) and the top-level `$set` and
 * `$set_once` person-property bags. Never drops an event (`null` in, `null`
 * out, as the contract requires).
 */
export function scrubDigestIssueIds(captureResult: CaptureResult | null): CaptureResult | null {
  if (!captureResult) return captureResult;
  const scrubbed: CaptureResult = {
    ...captureResult,
    properties: scrubIssueIdProperties(captureResult.properties ?? {}),
  };
  if (captureResult.$set) scrubbed.$set = scrubIssueIdProperties(captureResult.$set);
  if (captureResult.$set_once) scrubbed.$set_once = scrubIssueIdProperties(captureResult.$set_once);
  return scrubbed;
}
